import { useCallback, useEffect, useRef, useState } from 'react';
import {
  View, FlatList, TextInput, TouchableOpacity,
  StyleSheet, ActivityIndicator, KeyboardAvoidingView, Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { useMessages, markConversationRead } from '@/hooks/useMessages';
import { useSendMessage } from '@/hooks/useSendMessage';
import { Message } from '@/lib/types';
import { MessageBubble } from './MessageBubble';

/**
 * Visualizador de uma conversa (lista de mensagens + input).
 * Usado pela tela [conversationId] (mobile) e pelo ChatDesktop (master-detail).
 */
export function ConversationView({ conversationId }: { conversationId: string }) {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const { messages, isLoading } = useMessages(conversationId);
  const { sendText, sendPhoto, isSending } = useSendMessage(conversationId, profile?.id ?? '');
  const [inputText, setInputText] = useState('');
  const listRef = useRef<FlatList>(null);

  useEffect(() => {
    if (conversationId && profile?.id) {
      markConversationRead(conversationId, 'personal').then(() => {
        queryClient.invalidateQueries({ queryKey: ['conversations'] });
      });
    }
  }, [conversationId, profile?.id, queryClient]);

  useEffect(() => {
    if (messages.length > 0) listRef.current?.scrollToEnd({ animated: false });
  }, [messages.length]);

  async function handleSendText() {
    const text = inputText;
    setInputText('');
    await sendText(text);
    listRef.current?.scrollToEnd({ animated: true });
  }

  // useCallback pra renderMessage manter referencia estavel entre renders,
  // permitindo que MessageBubble (memoizado) realmente skipe re-renders.
  const renderMessage = useCallback(
    ({ item }: { item: Message }) => (
      <MessageBubble message={item} isOwn={item.sender_id === profile?.id} />
    ),
    [profile?.id],
  );

  if (isLoading) return <ActivityIndicator style={{ flex: 1 }} color="#C8102E" />;

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      keyboardVerticalOffset={88}
    >
      <FlatList
        ref={listRef}
        style={styles.list}
        contentContainerStyle={styles.listContent}
        data={messages}
        keyExtractor={(item) => item.id}
        renderItem={renderMessage}
        onContentSizeChange={() => listRef.current?.scrollToEnd({ animated: false })}
      />
      <View style={styles.inputRow}>
        <TouchableOpacity onPress={sendPhoto} style={styles.photoButton} disabled={isSending}>
          <Ionicons name="image-outline" size={24} color="#C8102E" />
        </TouchableOpacity>
        <TextInput
          style={styles.input}
          value={inputText}
          onChangeText={setInputText}
          placeholder="Mensagem..."
          placeholderTextColor="#aaa"
          multiline
          maxLength={1000}
        />
        <TouchableOpacity
          onPress={handleSendText}
          style={[styles.sendButton, (!inputText.trim() || isSending) && styles.sendButtonDisabled]}
          disabled={!inputText.trim() || isSending}
        >
          <Ionicons name="send" size={18} color="#fff" />
        </TouchableOpacity>
      </View>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#F5F5F5' },
  list: { flex: 1 },
  listContent: { padding: 16, paddingBottom: 8 },
  // Styles de mensagem migrados pra components/MessageBubble.tsx (AÇ-015)
  inputRow: {
    flexDirection: 'row', alignItems: 'flex-end', gap: 8,
    paddingHorizontal: 12, paddingVertical: 8,
    backgroundColor: '#fff', borderTopWidth: 1, borderTopColor: '#eee',
  },
  photoButton: { padding: 6 },
  input: {
    flex: 1, backgroundColor: '#F5F5F5', borderRadius: 20,
    paddingHorizontal: 14, paddingVertical: 8, fontSize: 15, color: '#1A1A1A', maxHeight: 100,
  },
  sendButton: {
    backgroundColor: '#C8102E', borderRadius: 20,
    width: 36, height: 36, alignItems: 'center', justifyContent: 'center',
  },
  sendButtonDisabled: { backgroundColor: '#ddd' },
});
